import React from 'react';
import { Slider } from '../../../components/Slider';
import { VFXEffectRegistry } from '../../../../effects/VFXEffectRegistry';
import { COLORS, FONT_SIZE, FONT_WEIGHT, SPACE, RADII } from '../../../theme';
import {
  TimelineTabProps, SectionLabel, SelectBox, ActionButton, InfoText,
  PrimitiveRow, eventBus, ACCENT, COUNTER_GREY,
} from './shared';
import { livePlayerPosition } from '../../../../systems/player/livePlayerPosition';

export const TimelineTab: React.FC<TimelineTabProps> = ({ ability, update, player }) => {
  // Read from metadata (persisted) instead of local state
  const entries: Array<{ at: number; type: string; effectId?: string; intensity?: number; duration?: number; color?: number }> = (ability as any).timeline || [];

  const setEntries = (newEntries: typeof entries) => {
    update({ timeline: newEntries } as any);
  };

  const addEntry = () => {
    setEntries([...entries, { at: 0, type: 'vfx', effectId: '' }]);
  };

  const removeEntry = (index: number) => {
    setEntries(entries.filter((_, i) => i !== index));
  };

  const updateEntry = (index: number, patch: Record<string, any>) => {
    const updated = [...entries];
    updated[index] = { ...updated[index], ...patch };
    setEntries(updated);
  };

  const allEffects = VFXEffectRegistry.getAllIds();

  return (
    <>
      <SectionLabel>Animation Timeline</SectionLabel>
      <InfoText style={{ marginTop: 0, marginBottom: 8 }}>
        Schedule VFX, screen effects, and projectiles at specific time offsets.
      </InfoText>

      {entries.map((entry, i) => (
        <PrimitiveRow key={i} style={{ marginBottom: 6 }}>
          <div style={{ display: 'flex', gap: SPACE.xs, alignItems: 'center', marginBottom: SPACE.xs }}>
            <span style={{ fontSize: FONT_SIZE.xs, color: ACCENT, fontWeight: FONT_WEIGHT.bold }}>#{i + 1}</span>
            <input
              type="number"
              title="Time offset (ms)"
              placeholder="0"
              value={entry.at}
              onChange={e => updateEntry(i, { at: parseInt(e.target.value) || 0 })}
              style={{ width: 60, background: COLORS.surfaceSunken, border: `1px solid ${COLORS.border}`, color: COLORS.textPrimary, padding: '2px 4px', borderRadius: RADII.sm, fontSize: FONT_SIZE.xs }}
            />
            <span style={{ fontSize: 9, color: COUNTER_GREY }}>ms</span>
            <SelectBox
              value={entry.type}
              onChange={e => updateEntry(i, { type: e.target.value })}
              style={{ width: 90, marginBottom: 0, fontSize: 10 }}
            >
              <option value="vfx">VFX</option>
              <option value="screenShake">Shake</option>
              <option value="screenFlash">Flash</option>
            </SelectBox>
            <ActionButton $variant="red" onClick={() => removeEntry(i)} style={{ width: 24, padding: '2px', marginBottom: 0, fontSize: 10 }}>
              ✕
            </ActionButton>
          </div>
          {entry.type === 'vfx' && (
            <SelectBox
              value={entry.effectId || ''}
              onChange={e => updateEntry(i, { effectId: e.target.value })}
              style={{ marginBottom: 0, fontSize: 10 }}
            >
              <option value="">-- Effect --</option>
              {allEffects.map(id => <option key={id} value={id}>{id}</option>)}
            </SelectBox>
          )}
          {entry.type === 'screenShake' && (
            <div style={{ display: 'flex', gap: SPACE.xs }}>
              <Slider label="Intensity" value={entry.intensity ?? 5} min={1} max={30} step={1}
                onChange={v => updateEntry(i, { intensity: v })} />
              <Slider label="Duration" value={entry.duration ?? 200} min={50} max={1000} step={50}
                onChange={v => updateEntry(i, { duration: v })} />
            </div>
          )}
          {entry.type === 'screenFlash' && (
            <div style={{ display: 'flex', gap: SPACE.xs }}>
              <Slider label="Duration" value={entry.duration ?? 200} min={50} max={1000} step={50}
                onChange={v => updateEntry(i, { duration: v })} />
            </div>
          )}
        </PrimitiveRow>
      ))}

      <ActionButton $variant="green" onClick={addEntry}>
        + Add Timeline Event
      </ActionButton>

      {entries.length > 0 && (
        <ActionButton $variant="blue" onClick={() => {
          const { x, y } = livePlayerPosition(player?.position ?? { x: 400, y: 300 });
          for (const entry of [...entries].sort((a, b) => a.at - b.at)) {
            setTimeout(() => {
              if (entry.type === 'vfx' && entry.effectId) {
                eventBus.emit('vfx:spawn', { effectId: entry.effectId, x, y });
              } else if (entry.type === 'screenShake') {
                eventBus.emit('vfx:screen_shake', { intensity: entry.intensity ?? 5, duration: entry.duration ?? 200 });
              } else if (entry.type === 'screenFlash') {
                eventBus.emit('vfx:screen_flash', { color: entry.color ?? 0xFFFFFF, duration: entry.duration ?? 200 });
              }
            }, entry.at);
          }
        }}>
          ▶ Preview Timeline ({Math.max(...entries.map(e => e.at), 0)}ms)
        </ActionButton>
      )}
    </>
  );
};
